import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { AssistantCitation, AssistantErrorCode, streamChat } from "../../api/assistant";
import { PageContext } from "./pageContext";
import { loadConversation, saveConversation } from "./storage";

export const MAX_HISTORY_TURNS = 6;

export type ChatMessage = {
  id: number;
  role: "user" | "assistant";
  content: string;
  citations?: AssistantCitation[];
  /** false when the backend said the help content did not cover the question */
  covered?: boolean;
  /** set once the answer has finished streaming */
  done?: boolean;
  error?: AssistantErrorCode | "network";
};

// ---- open/closed state shared between the nav button, the ? shortcut and the panel ----
let open = false;
const listeners = new Set<() => void>();
export function setAssistantOpen(value: boolean) {
  open = value;
  listeners.forEach((l) => l());
}
export function useAssistantOpen(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => open,
  );
}

// ---- a question handed to the panel from elsewhere (e.g. the docs page); the panel sends it once mounted ----
let pendingQuestion: string | null = null;
const pendingListeners = new Set<() => void>();
function setPendingQuestion(q: string | null) {
  pendingQuestion = q;
  pendingListeners.forEach((l) => l());
}
export function askAssistant(question: string) {
  setPendingQuestion(question);
  setAssistantOpen(true);
}
export function takePendingQuestion(): string | null {
  const q = pendingQuestion;
  if (q !== null) setPendingQuestion(null);
  return q;
}
/** The question waiting to be sent, so the panel reacts even when it is already open. */
export function usePendingQuestion(): string | null {
  return useSyncExternalStore(
    (cb) => {
      pendingListeners.add(cb);
      return () => pendingListeners.delete(cb);
    },
    () => pendingQuestion,
  );
}

function load(uuid: string | undefined): ChatMessage[] {
  const saved = loadConversation(uuid) as ChatMessage[];
  // anything that was mid-stream when the page reloaded is finished as-is
  return saved.map((m) => (m.role === "assistant" && !m.done ? { ...m, done: true } : m));
}

/**
 * History sent to the backend: drops failed or empty (stopped before any output) assistant turns together with the
 * user message they never answered, keeps the last MAX_HISTORY_TURNS, and always starts with a user turn.
 */
export function buildHistory(messages: ChatMessage[], question: string): { role: ChatMessage["role"]; content: string }[] {
  const unanswered = (m?: ChatMessage) => !!m && (!!m.error || (m.role === "assistant" && !m.content));
  const answered = messages.filter((m, i, all) => !unanswered(m) && !(m.role === "user" && unanswered(all[i + 1])));
  const history = [...answered, { role: "user" as const, content: question }]
    .slice(-MAX_HISTORY_TURNS)
    .map((m) => ({ role: m.role, content: m.content }));
  while (history[0]?.role === "assistant") history.shift();
  return history;
}

export function useAssistant(getContext: () => PageContext, userUuid?: string) {
  const [messages, setMessages] = useState<ChatMessage[]>(() => load(userUuid));
  const [streaming, setStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const nextId = useRef(messages.reduce((m, x) => Math.max(m, x.id), 0) + 1);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    saveConversation(userUuid, messages);
  }, [messages, userUuid]);

  const updateLast = useCallback((fn: (m: ChatMessage) => ChatMessage) => {
    setMessages((prev) => (prev.length ? [...prev.slice(0, -1), fn(prev[prev.length - 1]!)] : prev));
  }, []);

  const send = useCallback(
    async (text: string) => {
      const question = text.trim();
      if (!question || abortRef.current || messagesRef.current.some((m) => m.error === "rate_limited")) return;
      const history = buildHistory(messagesRef.current, question);
      setMessages((prev) => [
        ...prev,
        { id: nextId.current++, role: "user", content: question },
        { id: nextId.current++, role: "assistant", content: "" },
      ]);
      const controller = new AbortController();
      abortRef.current = controller;
      setStreaming(true);
      try {
        await streamChat(
          { messages: history, ...getContext() },
          (e) => {
            if (abortRef.current !== controller) return; // a reset replaced this stream
            switch (e.type) {
              case "citations":
                updateLast((m) => ({ ...m, citations: e.items }));
                break;
              case "delta":
                updateLast((m) => ({ ...m, content: m.content + e.text }));
                break;
              case "done":
                updateLast((m) => ({ ...m, covered: e.covered, done: true }));
                break;
              case "error":
                updateLast((m) => ({ ...m, error: e.code, done: true }));
                break;
            }
          },
          controller.signal,
        );
        if (abortRef.current === controller) updateLast((m) => (m.done ? m : { ...m, done: true }));
      } catch (_e) {
        if (abortRef.current !== controller) return;
        if (controller.signal.aborted) {
          updateLast((m) => ({ ...m, done: true }));
        } else {
          updateLast((m) => ({ ...m, error: "network", done: true }));
        }
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setStreaming(false);
        }
      }
    },
    [getContext, updateLast],
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setMessages([]);
  }, []);

  // the limit is per congregation per day and can't be retried, so stop accepting questions for this session
  const rateLimited = messages.some((m) => m.error === "rate_limited");

  return { messages, streaming, rateLimited, send, stop, reset };
}
